// axiosBaseQuery.ts
import type { BaseQueryFn } from "@reduxjs/toolkit/query";
import axios, { AxiosRequestConfig, AxiosError } from "axios";
const apiUrl = import.meta.env.VITE_API_URL;

export const axiosBaseQuery =
  (): BaseQueryFn<
    {
      url: string;
      method?: AxiosRequestConfig["method"];
      data?: AxiosRequestConfig["data"];
      params?: AxiosRequestConfig["params"];
      headers?: AxiosRequestConfig["headers"];
    },
    unknown,
    unknown
  > =>
    async ({ url, method = "GET", data, params, headers }) => {
      const accessToken = localStorage.getItem("accessToken");

      try {
        const result = await axios({
          url: apiUrl + url,
          method,
          data: data,
          params,
          headers: {
            "Content-Type": headers?.["Content-Type"] ?? "application/json",
            ...headers,
            Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
          },
        });

        return { data: result.data };
      } catch (error) {
        const err = error as AxiosError;

        // If token expired
        if (err.response?.status === 401 && !url.includes("auth/refreshToken") && !url.includes("auth/login")) {
          try {
            const refreshToken = localStorage.getItem("refreshToken");
            const refreshResponse = await axios.post(
              `${apiUrl}auth/refreshToken`,
              {
                refreshToken,
              }
            );

            const newAccessToken = refreshResponse.data.data.accessToken;
            localStorage.setItem("accessToken", newAccessToken);

            // Retry original request with new token
            const retryResult = await axios({
              url: apiUrl + url,
              method,
              data,
              params,
              headers: {
                ...headers,
                Authorization: `Bearer ${newAccessToken}`,
              },
            });

            return { data: retryResult.data };
          } catch {
            // Refresh failed — log out user or handle appropriately
            localStorage.removeItem("accessToken");
            localStorage.removeItem("refreshToken");
            return {
              error: {
                status: 401,
                data: "Unauthorized - Refresh Token Failed",
              },
            };
          }
        }

        return {
          error: {
            status: err.response?.status,
            data: err.response?.data || err.message,
          },
        };
      }
    };
